import { useId, useState } from 'react';
import { useTags } from '../api';

// Read-only chip list for tables and summaries.
export function TagList({ tags }: { tags: string[] | null | undefined }) {
  if (!tags || tags.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {tags.map((t) => (
        <span
          key={t}
          className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[11px] text-slate-600 dark:text-slate-400"
        >
          {t}
        </span>
      ))}
    </span>
  );
}

// Editable tags card for the household/person detail pages. Add and remove
// commit immediately via onSave (full replacement list) — no edit mode.
export function TagsCard({
  tags,
  onSave,
  saving,
  error,
}: {
  tags: string[];
  onSave: (next: string[]) => void;
  saving: boolean;
  error: Error | null;
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const listId = useId();
  const all = useTags();
  const suggestions = (all.data?.tags ?? [])
    .map((t) => t.name)
    .filter((n) => !tags.some((t) => t.toLowerCase() === n.toLowerCase()));

  const add = () => {
    const t = draft.trim();
    setDraft('');
    if (!t) { setAdding(false); return; }
    if (tags.some((x) => x.toLowerCase() === t.toLowerCase())) return;
    onSave([...tags, t]);
  };
  const remove = (t: string) => onSave(tags.filter((x) => x !== t));

  if (tags.length === 0 && !adding) {
    return (
      <div className="rounded border border-dashed border-slate-200 dark:border-slate-700 px-3 py-2 text-sm">
        <button
          className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
          onClick={() => setAdding(true)}
        >
          + Add tags
        </button>
      </div>
    );
  }

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-3 py-1">
        <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Tags</span>
        {!adding && (
          <button
            className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
            onClick={() => setAdding(true)}
          >
            + Add
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 px-3 py-2">
        {tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-xs text-slate-700 dark:text-slate-300"
          >
            {t}
            <button
              className="text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400"
              onClick={() => remove(t)}
              disabled={saving}
              title={`Remove "${t}"`}
            >
              ✕
            </button>
          </span>
        ))}
        {adding && (
          <span className="inline-flex items-center gap-1">
            <input
              className="w-36 rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs"
              type="text"
              list={listId}
              value={draft}
              placeholder="Tag name…"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') add();
                if (e.key === 'Escape') { setDraft(''); setAdding(false); }
              }}
              autoFocus
            />
            <datalist id={listId}>
              {suggestions.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
            <button
              className="rounded bg-slate-700 dark:bg-slate-600 px-2 py-0.5 text-xs text-white disabled:opacity-50"
              onClick={add}
              disabled={saving || !draft.trim()}
            >
              Add
            </button>
            <button
              className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400"
              onClick={() => { setDraft(''); setAdding(false); }}
              disabled={saving}
            >
              Done
            </button>
          </span>
        )}
      </div>
      {error && (
        <p className="border-t border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 px-3 py-1 text-xs text-rose-700 dark:text-rose-300">
          Save failed: {error.message}
        </p>
      )}
    </div>
  );
}
